import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import YearSelector from '../../../Common/Selectors/YearSelectorSingle';
import { creators as CashFlowActions } from '../../../Reducers/Reporting/CashFlow';
import TopChart from './TopChart';
import MiddleChart from './MiddleChart';
import BottomChart from './BottomChart';

const CashFlow = ({
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,
  selectedCompany,
  bubbleFlag,
}) => {
  const dispatch = useDispatch();
  const cashFlowSummary = useSelector(
    (state) => state?.Reporting?.Summary?.reportingSummaries?.cashFlow ?? '',
  );
  const topData = useSelector(
    (state) => state?.Reporting?.CashFlow?.topData ?? [],
  );
  const middleData = useSelector(
    (state) => state?.Reporting?.CashFlow?.middleData ?? [],
  );
  const bottomData = useSelector(
    (state) => state?.Reporting?.CashFlow?.bottomData ?? [],
  );
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const [selectedYears, setSelectedYears] = useState([defaultYear]);
  const [dimDate, setDimDate] = useState([defaultDimDate]);
  const [label, setLabel] = useState(defaultYear.toString());
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedBottomItems, setSelectedBottomItems] = useState([]);

  const triggerCashFlowRequests = useCallback(() => {
    dispatch(CashFlowActions.cashflowTopRequest([defaultYear]));
    dispatch(CashFlowActions.cashflowMiddleRequest([defaultDimDate]));
    dispatch(CashFlowActions.cashflowBottomRequest([defaultYear]));
  }, [defaultYear, defaultDimDate, dispatch]);

  // Effect to handle data fetching when dependencies change
  useEffect(() => {
    setSelectedYears([defaultYear]);
    setLabel(defaultYear.toString());
    setDimDate([defaultDimDate]);
    setSelectedTopItems([]);
    setSelectedBottomItems([]);

    const run = async () => {
      if (!lastFetchedCompany || !lastFetchedDimDate) {
        triggerCashFlowRequests();
      }
    };

    run();
  }, [
    defaultYear,
    defaultDimDate,
    lastFetchedCompany,
    lastFetchedDimDate,
    dispatch,
    triggerCashFlowRequests,
  ]);

  useEffect(() => {
    const run = async () => {
      const valuesChanged =
        lastFetchedCompany !== selectedCompany ||
        lastFetchedDimDate !== defaultDimDate;

      if (valuesChanged && lastFetchedCompany && lastFetchedDimDate) {
        triggerCashFlowRequests();
      }
    };

    run();
  }, [
    defaultYear,
    defaultMonth,
    defaultDimDate,
    selectedCompany,
    lastFetchedCompany,
    lastFetchedDimDate,
    dispatch,
    triggerCashFlowRequests,
  ]);

  const handleYear = async (event) => {
    if (event.selectedYears.length === 0) {
      toast.warning('Please select the year.');
      return;
    }

    setSelectedYears(event.selectedYears);
    setLabel(event.label);
    setSelectedTopItems([]);
    
    dispatch(CashFlowActions.cashflowTopRequest(event.selectedYears));
    dispatch(CashFlowActions.cashflowBottomRequest(event.selectedYears));
    dispatch(CashFlowActions.cashflowMiddleRequest(dimDate));
  };

  const handleFilter = async (event) => {
    if (event.selectedTopItems) {
      setSelectedTopItems(event.selectedTopItems);
    }

    if (event.selectedBottomItems) {
      setSelectedBottomItems(event.selectedBottomItems);
    }
  };

  const calculateChartDimensions = () => {
    let cashFlowGroupNameLengths = [];
    let cashFlowCategoryLengths = [];

    middleData.forEach((data) => {
      if (data.CashFlowGroup) {
        cashFlowGroupNameLengths.push(data.CashFlowGroup.length);
      }
      if (data.CashFlowCategory) {
        cashFlowCategoryLengths.push(data.CashFlowCategory.length);
      }
    });

    return {
      maxCashFlowGroupName: cashFlowGroupNameLengths.length > 0 
        ? Math.max(...cashFlowGroupNameLengths) + 3 
        : 0,
      maxCashFlowCategoryName: cashFlowCategoryLengths.length > 0 
        ? Math.max(...cashFlowCategoryLengths) 
        : 0
    };
  };

  const { maxCashFlowGroupName, maxCashFlowCategoryName } = calculateChartDimensions();

  const handleCashflowMiddleRequest = (date) => {
    dispatch(CashFlowActions.cashflowMiddleRequest(date));
  };
  
  return (
    <div>
      <div className="wrapper" id="cashflow_year_selector">
        {selectedYears.length > 0 && (
          <YearSelector
            selectedYears={selectedYears}
            label={label}
            onChange={handleYear}
          />
        )}
      </div>
      <div>
        <TopChart
          topData={topData}
          selectedYears={selectedYears}
          selectedTopItems={selectedTopItems}
          selectedBottomItems={selectedBottomItems}
          cashflowMiddleRequest={handleCashflowMiddleRequest}
          dimDate={dimDate}
          defaultStartMonth={defaultStartMonth}
          defaultMonth={defaultMonth}
          updateFilter={handleFilter}
        />
        <MiddleChart
          middleData={middleData}
          maxCashFlowGroupName={maxCashFlowGroupName}
          maxCashFlowCategoryName={maxCashFlowCategoryName}
        />
        <BottomChart
          bottomData={bottomData}
          selectedYears={selectedYears}
          selectedTopItems={selectedTopItems}
          selectedBottomItems={selectedBottomItems}
          updateFilter={handleFilter}
          cashflowMiddleRequest={handleCashflowMiddleRequest}
          defaultStartMonth={defaultStartMonth}
          defaultMonth={defaultMonth}
        />
      </div>

      {bubbleFlag && cashFlowSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={cashFlowSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

CashFlow.propTypes = {
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  selectedCompany: PropTypes.string.isRequired,
};

export default CashFlow;
